import { useStore } from '@nanostores/react';
import { useState } from 'react';
import type { PaymentMode } from '@dunea/shared';
import { chatId } from '~/lib/persistence/useChatHistory';
import {
  apiApproveReview,
  apiListReviews,
  apiRejectReview,
  apiReplyToReview,
  type ReviewStatus,
  type ReviewView,
} from '~/lib/api/reviews';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  Segmented,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { AdminActionsMenu } from '~/components/workbench/admin-extensions';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import {
  apiErrorMessage,
  applyReviewUpdate,
  emptyReviewsMessage,
  OWNER_PAGE_SIZE,
  ratingLabel,
  REPLY_MAX,
  replyError,
  REVIEW_FILTERS,
  reviewActions,
  reviewStatusMeta,
  starStates,
  subjectLabel,
  type ReviewAction,
} from './reviews-model';

/**
 * Owner-facing Reviews plugin panel: moderate reviews visitors leave on the
 * site (nothing shows until approved) and reply publicly. The app id IS the
 * chat id; reviews left in the preview are test mode.
 */
export function ReviewsPanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      <div className="border-b border-hairline px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:star text-[13px] text-accent" />
          </div>
          <h3 className="text-[13px] font-medium tracking-tight text-primary">Reviews</h3>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted">
          Star ratings from your visitors. A review shows on your site only after you approve it.
        </p>
      </div>

      {appId ? (
        <ReviewsPanelBody key={appId} appId={appId} />
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
          Start building to collect reviews.
        </div>
      )}
    </div>
  );
}

function ReviewsPanelBody({ appId }: { appId: string }) {
  const [status, setStatus] = useState<ReviewStatus>('pending');
  const [mode, setMode] = useState<PaymentMode>('live');
  const reviews = useResource(() => apiListReviews(appId, { status, mode }), `${appId}:${status}:${mode}`);

  const filters = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Segmented label="Review status" value={status} options={REVIEW_FILTERS} onChange={setStatus} />
      <Segmented
        label="Mode"
        value={mode}
        options={[
          ['test', 'Test'],
          ['live', 'Live'],
        ]}
        onChange={setMode}
      />
    </div>
  );

  return (
    <div className="flex-1 space-y-6 overflow-y-auto p-4">
      <Section title="Reviews" icon="i-ph:star" aside={filters}>
        {reviews.loading && !reviews.data ? (
          <>
            <SkeletonCard lines={3} />
            <SkeletonCard lines={2} />
          </>
        ) : reviews.error ? (
          <LoadError message="Could not load reviews." onRetry={() => void reviews.reload()} />
        ) : !reviews.data?.length ? (
          <div className={classNames(CARD, 'space-y-1.5 px-4 py-6 text-center')}>
            <div className="i-ph:star mx-auto text-xl text-faint" />
            <p className="text-[12px] leading-relaxed text-muted">{emptyReviewsMessage(status, mode)}</p>
          </div>
        ) : (
          <>
            {reviews.data.map((r) => (
              <ReviewCard
                key={r.id}
                appId={appId}
                review={r}
                onUpdated={(next) => reviews.setData((list) => applyReviewUpdate(list ?? [], next, status))}
              />
            ))}
            {reviews.data.length >= OWNER_PAGE_SIZE && (
              <p className="text-center text-[11px] text-muted">Showing the newest {OWNER_PAGE_SIZE}.</p>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-px" role="img" aria-label={ratingLabel(rating)}>
      {starStates(rating).map((on, i) => (
        <span
          key={i}
          className={classNames(on ? 'i-ph:star-fill text-warning' : 'i-ph:star text-faint', 'text-[13px]')}
        />
      ))}
    </span>
  );
}

function ReviewCard({
  appId,
  review,
  onUpdated,
}: {
  appId: string;
  review: ReviewView;
  onUpdated: (next: ReviewView) => void;
}) {
  const [busy, setBusy] = useState<ReviewAction | 'reply' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState(review.ownerReply ?? '');
  const meta = reviewStatusMeta(review.status);
  const replyProblem = replyError(reply);

  const moderate = async (action: ReviewAction) => {
    setBusy(action);
    setError(null);

    try {
      const next = await (action === 'approve' ? apiApproveReview : apiRejectReview)(appId, review.id);
      onUpdated(next);
    } catch (err) {
      setError(apiErrorMessage(err, action === 'approve' ? 'Could not approve the review.' : 'Could not reject it.'));
      setBusy(null);
    }
  };

  const saveReply = async (e: React.FormEvent) => {
    e.preventDefault();

    if (replyProblem) {
      return;
    }

    setBusy('reply');
    setError(null);

    try {
      const next = await apiReplyToReview(appId, review.id, reply.trim());
      setReply(next.ownerReply ?? '');
      setReplying(false);
      onUpdated(next);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the reply.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className={classNames(CARD, 'space-y-2.5 p-4')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <Stars rating={review.rating} />
          {review.title && <h4 className="text-[13px] font-medium text-primary">{review.title}</h4>}
        </div>
        <Pill tone={meta.tone} label={meta.label} />
      </div>

      {review.body && <p className="whitespace-pre-line text-[12px] leading-relaxed text-secondary">{review.body}</p>}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted">
        <span className="text-secondary">{review.authorName}</span>
        {review.verified && <Pill tone="success" label="Verified buyer" />}
        <span aria-hidden>·</span>
        <span className="font-geist-mono" title={review.subject}>
          {subjectLabel(review.subject)}
        </span>
        <span aria-hidden>·</span>
        <span>{formatDate(review.createdAt)}</span>
      </div>

      {review.ownerReply && !replying && (
        <div className="rounded-lg border border-hairline bg-sunken px-3 py-2">
          <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted">Your reply</div>
          <p className="mt-1 whitespace-pre-line text-[12px] leading-relaxed text-secondary">{review.ownerReply}</p>
        </div>
      )}

      {replying && (
        <form onSubmit={saveReply} className="space-y-2">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            maxLength={REPLY_MAX + 100}
            placeholder="Thank you! Shown under the review on your site."
            aria-label="Reply"
            className="ui-input resize-y"
            autoFocus
          />
          {replyProblem && <p className="text-[11px] text-danger/90">{replyProblem}</p>}
          <div className="flex items-center gap-2">
            <button type="submit" disabled={busy !== null || Boolean(replyProblem)} className={BTN_SECONDARY}>
              {busy === 'reply' && <Spinner />}
              {reply.trim() ? 'Save reply' : review.ownerReply ? 'Remove reply' : 'Save reply'}
            </button>
            <button
              type="button"
              onClick={() => {
                setReplying(false);
                setReply(review.ownerReply ?? '');
              }}
              disabled={busy !== null}
              className={BTN_GHOST}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && <ErrorBanner message={error} />}

      {!replying && (
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-2.5">
          {reviewActions(review.status).map((action) => (
            <button
              key={action}
              onClick={() => void moderate(action)}
              disabled={busy !== null}
              className={action === 'approve' ? BTN_SECONDARY : BTN_GHOST}
            >
              {busy === action ? (
                <Spinner />
              ) : (
                <div className={classNames(action === 'approve' ? 'i-ph:check' : 'i-ph:x', 'text-[13px]')} />
              )}
              {action === 'approve' ? 'Approve' : 'Reject'}
            </button>
          ))}
          <button
            onClick={() => setReplying(true)}
            disabled={busy !== null}
            className={classNames(BTN_GHOST, 'ml-auto')}
          >
            <div className="i-ph:chat-circle text-[13px]" />
            {review.ownerReply ? 'Edit reply' : 'Reply'}
          </button>
          <AdminActionsMenu appId={appId} target="reviews.review.actions" resourceId={review.id} compact />
        </div>
      )}
    </article>
  );
}
